import React, { useEffect, useState } from 'react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { BillingService, type SubscriptionSnapshotDto } from '@/services/BillingService';
import { useOrgStore } from '@/stores/orgStore';
import { usePaywallCheck } from '@/hooks/usePaywallCheck';
import { usePaywallStore } from '@/stores/paywallStore';
import { useCreditStore } from '@/stores/creditStore';
import { Link } from 'react-router-dom';

const billingService = new BillingService();

export function OrgBilling() {
  const orgId = useOrgStore((s) => s.activeOrgId);
  const [subscription, setSubscription] = useState<SubscriptionSnapshotDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [endTrialLoading, setEndTrialLoading] = useState(false);
  const { isCancelledTrial, isExhaustedTrial, currentPeriodEnd } = usePaywallCheck();
  const openPaywall = usePaywallStore((s) => s.openPaywall);

  useEffect(() => {
    setLoading(true);
    billingService.getSubscription()
      .then(setSubscription)
      .catch(() => setSubscription(null))
      .finally(() => setLoading(false));
  }, [orgId]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await billingService.createPortalSession();
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to open customer portal:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleEndTrialNow = async () => {
    setEndTrialLoading(true);
    try {
      await billingService.endTrialNow();
      // Refresh credit balance and subscription
      useCreditStore.getState().reset();
      useCreditStore.getState().fetchBalance();
      // Reload subscription data
      const sub = await billingService.getSubscription();
      setSubscription(sub);
    } catch (err: any) {
      console.error('Failed to end trial:', err);
      alert(err.message || 'Failed to start plan. Please try again.');
    } finally {
      setEndTrialLoading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      </PageContainer>
    );
  }

  const statusBadge = (status: string | undefined) => {
    switch (status) {
      case 'active': return <Badge variant="success">Active</Badge>;
      case 'trialing': return <Badge variant="default">Trial</Badge>;
      case 'past_due': return <Badge variant="warning">Past Due</Badge>;
      case 'canceled': return <Badge variant="secondary">Canceled</Badge>;
      case 'unpaid': return <Badge variant="warning">Unpaid</Badge>;
      default: return <Badge variant="secondary">Free</Badge>;
    }
  };

  // Show portal/manage options only if user has a Stripe customer record
  const hasStripeHistory = !!subscription?.stripeCustomerId;

  let formattedDate = 'soon';
  if (currentPeriodEnd) {
    const endDate = new Date(currentPeriodEnd);
    const diffDays = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    const dateStr = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    formattedDate = diffDays <= 0 ? 'today' : diffDays === 1 ? `tomorrow (${dateStr})` : diffDays <= 14 ? `in ${diffDays} days (${dateStr})` : dateStr;
  }

  return (
    <PageContainer>
      <PageHeader
        title="Billing & Subscription"
        description="View your subscription plan and billing details"
      />

      {/* Scenario B: Cancelled trial banner */}
      {isCancelledTrial && (
        <div className="mt-6 flex items-start gap-3 text-sm text-warning-800 bg-warning-50 border border-warning-200 rounded-lg px-4 py-3">
          <svg className="w-5 h-5 shrink-0 mt-0.5 text-warning-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="font-semibold">You cancelled your free trial</p>
            <p className="text-warning-700 mt-0.5">Your credits have been removed. Subscribe to continue creating.</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={openPortal} disabled={portalLoading}>
                {portalLoading ? 'Opening…' : 'Reactivate'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => openPaywall('billing', 'cancelled_trial')}>
                View Plans
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Scenario C: Exhausted trial banner */}
      {isExhaustedTrial && (
        <div className="mt-6 flex items-start gap-3 text-sm text-info-800 bg-info-50 border border-info-200 rounded-lg px-4 py-3">
          <svg className="w-5 h-5 shrink-0 mt-0.5 text-info-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="font-semibold">You've used all your trial credits</p>
            <p className="text-info-700 mt-0.5">Your plan starts on {formattedDate} with 4,000 credits.</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={handleEndTrialNow} disabled={endTrialLoading}>
                {endTrialLoading ? 'Starting plan…' : 'Start Plan Now'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
        </CardHeader>
        <CardContent>
          {subscription ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold">{subscription.plan?.name || 'Free'}</span>
                {statusBadge(subscription.status)}
              </div>

              {subscription.currentPeriodEnd && (
                <div className="text-sm text-muted-foreground">
                  {subscription.status === 'trialing'
                    ? `Trial ends ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                    : `Current period ends ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                  }
                </div>
              )}

              {subscription.cancelAtPeriodEnd && !isCancelledTrial && (
                <div className="flex items-center gap-2 text-sm text-warning-600 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Your subscription will cancel at the end of the current billing period.
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {hasStripeHistory && (
                  <Button onClick={openPortal} disabled={portalLoading}>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    {portalLoading ? 'Opening…' : 'Manage Subscription'}
                  </Button>
                )}
                <Link to="/pricing">
                  <Button variant="outline">View Plans</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No active subscription found.</p>
              <Link to="/pricing">
                <Button>Choose a Plan</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Portal info — only shown to users with Stripe history */}
      {hasStripeHistory && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Customer Portal</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              From the customer portal you can:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-sm font-medium">Update payment method</p>
                  <p className="text-xs text-muted-foreground">Change your card or payment details</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-sm font-medium">View invoice history</p>
                  <p className="text-xs text-muted-foreground">Download past invoices and receipts</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-sm font-medium">Cancel subscription</p>
                  <p className="text-xs text-muted-foreground">Cancel at the end of your current billing period</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-sm font-medium">Update billing details</p>
                  <p className="text-xs text-muted-foreground">Change your name, email, or address on invoices</p>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Button variant="outline" onClick={openPortal} disabled={portalLoading}>
                {portalLoading ? 'Opening…' : 'Open Customer Portal'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Related Links */}
      <Card className="mt-6">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/organization/credits" className="text-primary-600 hover:text-primary-700 underline">Credits & Usage</Link>
            <Link to="/pricing" className="text-primary-600 hover:text-primary-700 underline">View Plans & Pricing</Link>
            <Link to="/refund-policy" className="text-primary-600 hover:text-primary-700 underline">Refund Policy</Link>
            <Link to="/terms" className="text-primary-600 hover:text-primary-700 underline">Terms of Service</Link>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
